/*
 * Getters for the [users store]{link module:store/users}
 *
 * @module store/users/getters
 */

import { uniq } from 'lodash'
import { SUPPORTED_PERM_ROLES_FOR_VIEW, permissionPerTypeFromFlag } from '@/store/modules/users'
import { PERMISSION_FLAGS } from '@/store/modules/users'
import i18n from '@/i18n'
import { sortSelectOptions } from '@/components/helpers'

/**
 * Getter to get Information for a single User
 *
 * @function
 *
 * @return {function}
 */
export const getUser = (state) => {
  return (id) => {
    return state.users[id]
  }
}

export const groupIds = (state) => {
  return Object.keys(state.emsGroups).map((i) => parseInt(i))
}

export const userIdsOfGroups = (state) => {
  let userIds = []
  for (const gId in state.emsGroups) {
    userIds = userIds.concat(Object.keys(state.emsGroups[gId]))
  }

  return uniq(userIds).map((i) => parseInt(i))
}

/**
 * Getter for one directly related User information for a given EmsId and role
 *
 * @function
 *
 * @return {function} which returns for a `emsId, userId, role`: {object[]} => { id: {integer}, email: {string} , role: {string}, read: {integer}, create: {integer}, ...}`
 */
export const getUserForEmsDirect = (state) => {
  return (emsId, userId, role) => {
    if (!state.emss.direct[emsId]) {
      return null
    }

    let returnUsers
    if (state.emss.direct[emsId][userId]) {
      if (state.users[userId].roles.includes(role)) {
        if (Object.keys(state.emss.direct[emsId][userId]).includes(role)) {
          returnUsers = {
            id: parseInt(userId),
            email: state.users[userId].email,
            role,
            ...permissionPerTypeFromFlag(state.emss.direct[emsId][userId][role])
          }
        }
      }
    }

    return returnUsers
  }
}

/**
 * Getter to all directly related User information for a given EmsId
 *
 * @function
 *
 * @return {function} which returns for a `emsId`: {object[]} => { id: {integer}, email: {string} , role: {string}, read: {integer}, create: {integer}, ...}`
 */
export const getUsersForEmsDirect = (state) => {
  return (emsId) => {
    if (!state.emss.direct[emsId]) {
      return []
    }

    const returnUsers = []
    for (const userId in state.emss.direct[emsId]) {
      for (const role of state.users[userId].roles) {
        if (Object.keys(state.emss.direct[emsId][userId]).includes(role)) {
          returnUsers.push({
            id: parseInt(userId),
            email: state.users[userId].email,
            role,
            ...permissionPerTypeFromFlag(state.emss.direct[emsId][userId][role])
          })
        }
      }
    }

    return returnUsers
  }
}

/**
 * Getter to all directly related User information for a given EmsId
 *
 * @function
 *
 * @return {function} which returns for a `emsId`: {object[]} => { id: {integer}, email: {string} , role: {string}, read: {integer}, create: {integer}, ...}`
 */
export const getUsersForEmsDirectWithDummies = (state) => {
  return (emsId, viewRoles) => {
    if (!state.emss.direct[emsId]) {
      return []
    }
    const returnUsers = []
    for (const userId in state.emss.direct[emsId]) {
      for (const role of viewRoles) {
        if (Object.keys(state.emss.direct[emsId][userId]).includes(role)) {
          returnUsers.push({
            id: parseInt(userId),
            email: state.users[userId].email,
            role,
            ...permissionPerTypeFromFlag(state.emss.direct[emsId][userId][role])
          })
        } else {
          returnUsers.push({
            id: parseInt(userId),
            email: state.users[userId].email,
            role,
            ...permissionPerTypeFromFlag(0)
          })
        }
      }
    }

    return returnUsers
  }
}

/**
 * Getter to get all directly related Users without roles for a given EmsId
 *
 * @function
 *
 * @return {function} which returns for a `emsId`: {object[]} => { id: {integer}, email: {string} }`
 */
export const getUsersWithoutRolesForEmsDirect = (state) => {
  return (emsId) => {
    if (!state.emss.direct[emsId]) {
      return {}
    }

    const returnUsers = {}
    for (const userId in state.emss.direct[emsId]) {
      if (!state.users[userId].roles.length) {
        returnUsers[userId] = {
          id: parseInt(userId),
          email: state.users[userId].email
        }
      }
    }
    return returnUsers
  }
}

/**
 * Getter to all Userinformation for a given EmsId through emsGroups
 *
 * @function
 *
 * @return {function} which returns for a `emsId`: {object[]} => { id: {integer}, email: {string} , role: {string}, emsGroupIds: {integer[]}, read: {integer}, create: {integer}, ...}`
 */
export const getUsersForEmsThroughGroups = (state) => {
  return (emsId) => {
    if (!state.emss.throughGroups[emsId]) {
      return []
    }
    const returnUsers = []
    for (const userId in state.emss.throughGroups[emsId]) {
      for (const role of state.users[userId].roles) {
        if (state.emss.throughGroups[emsId][userId][role]) {
          returnUsers.push({
            id: parseInt(userId),
            email: state.users[userId].email,
            role,
            emsGroupIds: state.emss.throughGroups[emsId][userId].emsGroupIds,
            ...permissionPerTypeFromFlag(state.emss.throughGroups[emsId][userId][role])
          })
        }
      }
    }

    return returnUsers
  }
}

/**
 * Getter to all Userinformation for a given groupId
 *
 * if param roles Array{String} is set, only included roles will be gathered
 *
 * @function
 *
 * @return {function} which returns for a `groupId`: {object[]} => { id: {integer}, email: {string} , role: {string}, read: {integer}, create: {integer}, ...}`
 */
export const getUsersForGroup = (state) => {
  return (groupId, roles = null) => {
    if (!state.emsGroups[groupId]) {
      return []
    }

    const returnUsers = []
    for (const userId in state.emsGroups[groupId]) {
      if (state.users[userId]) {
        for (const role of state.users[userId].roles) {
          if (roles && !roles.includes(role)) {
            continue
          }
          if (Object.keys(state.emsGroups[groupId][userId]).includes(role)) {
            returnUsers.push({
              id: parseInt(userId),
              email: state.users[userId].email,
              role,
              ...permissionPerTypeFromFlag(state.emsGroups[groupId][userId][role])
            })
          }
        }
      }
    }

    return returnUsers
  }
}

/**
 * Getter to all Userinformation for a given groupId and Users without a role
 *
 * @function
 *
 * @return {function} which returns for a `groupId`: {object[]} => { id: {integer}, email: {string} , role: {string}, read: {integer}, create: {integer}, ...}`
 */
export const getUsersWithoutRolesForGroup = (state) => {
  return (groupId) => {
    if (!state.emsGroups[groupId]) {
      return {}
    }

    const returnUsers = {}
    for (const userId in state.emsGroups[groupId]) {
      if (state.users[userId]) {
        if (state.users[userId].roles && !state.users[userId].roles.length) {
          returnUsers[userId] = {
            id: parseInt(userId),
            email: state.users[userId].email
          }
        }
      }
    }

    return returnUsers
  }
}

/**
 * Getter for all Userinformation for given emsGroupIds
 *
 * Permissions for one user between different groups will be joined
 *
 * @function
 *
 * @return {function} which returns for a `groupIds`: {object[]} => { id: {integer}, email: {string} , role: {string}, read: {integer}, create: {integer}, ...}`
 */
export const getUsersForGroups = (state) => {
  return (emsGroupIds) => {
    const groupUsers = {}
    for (const emsGroupId of emsGroupIds) {
      if (state.emsGroups[emsGroupId]) {
        for (const userId of Object.keys(state.emsGroups[emsGroupId])) {
          if (!groupUsers[userId]) {
            groupUsers[userId] = {
              id: parseInt(userId),
              email: state.users[userId].email
            }
            for (const role of state.users[userId].roles) {
              groupUsers[userId][role] = 0
            }
          }

          for (const role of state.users[userId].roles) {
            if (Object.keys(state.emsGroups[emsGroupId][userId]).includes(role)) {
              groupUsers[userId][role] = joinPermissions(
                groupUsers[userId][role],
                state.emsGroups[emsGroupId][userId][role]
              )
            }
          }
        }
      }
    }
    const returnUsers = []
    for (const userId of Object.keys(groupUsers)) {
      for (const role of state.users[userId].roles) {
        returnUsers.push({
          id: parseInt(userId),
          email: groupUsers[userId].email,
          role,
          ...permissionPerTypeFromFlag(groupUsers[userId][role])
        })
      }
    }

    return returnUsers
  }
}

/**
 * Getter for permissions of groups filtered by SUPPORTED_PERM_ROLES_FOR_VIEW.
 *
 *
 * Helper for set_group_permissions/set. returns table structure
 *
 * @function
 *
 * @return {object} of typedef: `{ id: {integer}, name: {string}, role: {string}, read: {integer}, create: {integer},...}`
 */
export const groupPermissionsForRoles = (state, getters, rootState) => {
  const permissions = []

  for (const groupId in state.emsGroups) {
    const presentRoles = []
    for (const userId in state.emsGroups[groupId]) {
      for (const role in state.emsGroups[groupId][userId]) {
        if (!presentRoles.includes(role) && SUPPORTED_PERM_ROLES_FOR_VIEW.includes(role)) {
          permissions.push({
            id: groupId,
            name: rootState.energyManagementSystemGroups.emsGroups.hash[groupId].name,
            role,
            ...permissionPerTypeFromFlag(state.emsGroups[groupId][userId][role])
          })

          presentRoles.push(role)
        }
      }
    }
  }

  return permissions
}

function joinPermissions(perm1, perm2) {
  return perm1 | perm2
}

/*
 * Getter to all Group permission information for a given userId
 *
 * @function
 *
 * @return {function} which returns for a `userId`: {object[]} => { id: {integer}, name: {string} , role: {string}, read: {integer}, create: {integer}, ...}`
 */
export const getGroupPermissionsForUser = (state, getters, rootState) => {
  return (userId) => {
    const returnPerms = []
    for (const groupId in state.emsGroups) {
      for (const role of state.users[userId].roles) {
        if (state.emsGroups[groupId][userId]) {
          if (state.emsGroups[groupId][userId][role]) {
            returnPerms.push({
              id: parseInt(groupId),
              name: rootState.energyManagementSystemGroups.emsGroups.hash[groupId].name,
              role,
              ...permissionPerTypeFromFlag(state.emsGroups[groupId][userId][role])
            })
          }
        }
      }
    }

    return returnPerms
  }
}

/*
 * Getter to get all Groups for a given userId without taking permissions into account
 *
 * @function
 *
 * @return {function} which returns for a `groupId`: {object[]} => { id: {integer}, name: {string} }`
 */
export const getIncludingGroupsForUser = (state, getters, rootState) => {
  return (userId) => {
    const returnGroups = {}
    for (const groupId in state.emsGroups) {
      if (state.emsGroups[groupId][userId]) {
        if (rootState.energyManagementSystemGroups.emsGroups.hash[groupId]) {
          returnGroups[groupId] = {
            id: parseInt(groupId),
            name: rootState.energyManagementSystemGroups.emsGroups.hash[groupId].name
          }
        }
      }
    }

    return returnGroups
  }
}

/*
 * Getter to all direct EMS information for a given userId
 *
 * @function
 *
 * @return {function} which returns for a `userId`: {object[]} => { id: {integer}, name: {string} , role: {string}, read: {integer}, create: {integer}, ...}`
 */
export const getDirectEmsPermissionsForUser = (state, getters, rootState) => {
  return (userId) => {
    const returnPerms = []
    for (const emsId in state.emss.direct) {
      for (const role of state.users[userId].roles) {
        if (state.emss.direct[emsId][userId]) {
          if (state.emss.direct[emsId][userId][role]) {
            returnPerms.push({
              id: parseInt(emsId),
              name: rootState.energyManagementSystems.emss[emsId].name,
              role,
              ...permissionPerTypeFromFlag(state.emss.direct[emsId][userId][role])
            })
          }
        }
      }
    }

    return returnPerms
  }
}

/**
 * Getter to get all user info for a given emsGroupId without any permission information
 *
 * Array{object} => {id, email, roles}
 *
 * @function
 *
 * @return {function}
 */
export const getUsersForGroupWithoutPerm = (state) => {
  return (emsGroupId) => {
    if (!state.emsGroups[emsGroupId]) {
      return {}
    }

    const users = {}
    for (const userId in state.emsGroups[emsGroupId]) {
      users[userId] = {
        id: parseInt(userId),
        email: state.users[userId].email,
        roles: state.users[userId].roles
      }
    }
    return users
  }
}

/*
 * Getter to all Group permission information for a given userId
 *
 * @function
 *
 * @return {Permission}
 */
export const getGroupPermissionForUser = (state) => {
  return ({ userId, groupId }) => {
    const returnPerms = []
    if (!state.users[userId]) {
      return returnPerms
    }
    if (state.emsGroups[groupId][userId]) {
      return state.emsGroups[groupId][userId]
    }
    return {}
  }
}

/**
 * Getter to get all user info for a given Community without any permission information
 *
 * Array{object} => {id, email, roles}
 *
 * @function
 *
 * @return {function}
 */
export const getUsersForCommunityWithoutPerm = (state, getters, rootState) => {
  return (communityId) => {
    const groupId = rootState['communities'][communityId].rootGroupId

    if (!state.emsGroups[groupId]) {
      return {}
    }

    const users = {}
    for (const userId in state.emsGroups[groupId]) {
      users[userId] = {
        id: parseInt(userId),
        email: state.users[userId].email,
        roles: state.users[userId].roles
      }
    }
    return users
  }
}

/**
 * Getter to get all users addable to a community as MultiSelect Options
 *
 * Gets all users and returns them without the Users included in the Community
 *
 * @function
 *
 * @params {integer} communityId
 *
 * @return {function}
 */
export const getUsersForAddToCommunitySelect = (state, getters, rootState) => {
  return (communityId) => {
    const groupId = rootState['communities'][communityId].rootGroupId

    if (!groupId || !state.emsGroups[groupId]) {
      return []
    }
    const options = []

    const groupUserIds = Object.keys(state.emsGroups[groupId])

    for (const userId in state.users) {
      if (!groupUserIds.includes(userId)) {
        options.push({
          value: userId,
          label: state.users[userId].email
        })
      }
    }
    return sortSelectOptions(options, { sortByProperty: 'label' })
  }
}

/**
 * Getter to get all users removable from a community as MultiSelect Options
 *
 * Returns all users included in the Community
 *
 * @function
 *
 * @params {integer} communityId
 *
 * @return {function}
 */
export const getUsersForRemoveFromCommunitySelect = (state, getters, rootState) => {
  return (communityId) => {
    const groupId = rootState['communities'][communityId].rootGroupId

    if (!groupId || !state.emsGroups[groupId]) {
      return []
    }
    const options = []

    for (const userId in state.emsGroups[groupId]) {
      options.push({
        value: userId,
        label: state.users[userId].email
      })
    }
    return sortSelectOptions(options, { sortByProperty: 'label' })
  }
}

/**
 * Getter to get all users in a community as MultiSelect Options
 *
 * Returns all users included in the Community ignoring the dummy user with id -1
 *
 * @function
 *
 * @params {integer} communityId
 *
 * @return {function}
 */
export const getUsersForCommunitySelect = (state, getters, rootState) => {
  return (communityId) => {
    if (!rootState['communities'][communityId]) {
      return []
    }

    const groupId = rootState['communities'][communityId].rootGroupId

    if (!groupId || !state.emsGroups[groupId]) {
      return []
    }
    const options = []

    for (const userId in state.emsGroups[groupId]) {
      if (userId !== '-1') {
        options.push({
          value: userId,
          label: state.users[userId].email
        })
      }
    }
    return sortSelectOptions(options, { sortByProperty: 'label' })
  }
}

/**
 * Getter to get the TableHeader for email and all groups in the community
 *
 * @function
 *
 * @params {integer} communityId
 *
 * @return {function}
 */
export const getCommunityUserPermissionTableHeader = (state, getters, rootState, rootGetters) => {
  return (communityId) => {
    const rootId = rootState['communities'][communityId].rootGroupId

    const groupIds = rootGetters['energyManagementSystemGroups/getGroupIdsFromRoot'](rootId)

    const columns = [
      {
        key: 'email',
        label: 'User'
      }
    ]
    for (const groupId of groupIds) {
      if (rootState.energyManagementSystemGroups.emsGroups.hash[groupId]) {
        columns.push({
          key: String(groupId),
          label: rootState.energyManagementSystemGroups.emsGroups.hash[groupId].name
        })
      }
    }
    return columns
  }
}

/**
 * Getter for the permission Table Entries
 *
 * Returns the users email together with the PermTags 'H'|'S'|'A' for each group in the community
 *
 * @function
 *
 * @params {integer} communityId
 *
 * @return {function}
 */
export const getCommunityUserPermissionTable = (state, getters, rootState, rootGetters) => {
  return (communityId) => {
    const rootId = rootState['communities'][communityId].rootGroupId

    const groupIds = rootGetters['energyManagementSystemGroups/getGroupIdsFromRoot'](rootId)

    const users = {}

    for (const groupId of groupIds) {
      if (state.emsGroups[groupId]) {
        for (const userId in state.emsGroups[groupId]) {
          if (userId === '-1') continue

          if (!users[userId]) {
            users[userId] = {}
            users[userId].email = state.users[userId].email
          }
          users[userId][String(groupId)] = permissionTagFromPerms(state.emsGroups[groupId][userId])
        }
      }
    }
    const userOptions = Object.values(users)

    for (const option of userOptions) {
      for (const groupId of groupIds) {
        if (!option[groupId]) {
          option[groupId] = 'H'
        }
      }
    }

    return userOptions
  }
}

/**
 * Getter to get the TableHeader for all groups in the community
 *
 * @function
 *
 * @params {integer} communityId
 *
 * @return {function}
 */
export const getCommunityPermissionSummaryTableHeader = (state, getters, rootState, rootGetters) => {
  return (communityId) => {
    const rootId = rootState['communities'][communityId].rootGroupId

    const groupIds = rootGetters['energyManagementSystemGroups/getGroupIdsFromRoot'](rootId)

    const columns = []
    for (const groupId of groupIds) {
      if (rootState.energyManagementSystemGroups.emsGroups.hash[groupId]) {
        columns.push({
          key: String(groupId),
          label: rootState.energyManagementSystemGroups.emsGroups.hash[groupId].name
        })
      }
    }
    return columns
  }
}

/**
 * Getter for the permission Table Entries for a single userId
 *
 * Returns the users PermTags 'H'|'S'|'A' for each group in the community
 *
 * @function
 *
 * @params {integer} communityId
 * @params {integer} userId
 *
 * @return {function}
 */
export const getCommunityPermissionSummaryTable = (state, getters, rootState, rootGetters) => {
  return (communityId, userId) => {
    const rootId = rootState['communities'][communityId].rootGroupId

    const groupIds = rootGetters['energyManagementSystemGroups/getGroupIdsFromRoot'](rootId)

    const user = {}

    for (const groupId of groupIds) {
      if (state.emsGroups[groupId]) {
        if (state.emsGroups[groupId][userId]) {
          user[String(groupId)] = permissionTagFromPerms(state.emsGroups[groupId][userId])
        }
      }
    }
    return [user]
  }
}

/**
 * Helper Function to translate the permissions of a user into PermTags
 *
 * Returns the users PermTags 'H'|'S'|'A' for each group in the community
 *
 * Customer: !read AND Admin: !orgRead && !orgWrite => 'H'
 * Customer: read AND Admin: !orgRead && !orgWrite => 'S'
 * Customer: read AND Admin: orgRead && orgWrite => 'A'
 *
 * @function
 *
 * @params {Permissions} perms
 *
 * @return {String} permTag 'H'|'S'|'A'|''
 */
const permissionTagFromPerms = (perms) => {
  const t = i18n.global.t

  if (
    perms['customer'] & PERMISSION_FLAGS['read'] &&
    perms['admin'] & PERMISSION_FLAGS['orgRead'] &&
    perms['admin'] & PERMISSION_FLAGS['orgWrite']
  ) {
    return t('api.community_management.permissions.admin')
  }

  if (
    perms['customer'] & PERMISSION_FLAGS['read'] &&
    !(perms['admin'] & PERMISSION_FLAGS['orgRead']) &&
    !(perms['admin'] & PERMISSION_FLAGS['orgWrite'])
  ) {
    return t('api.community_management.permissions.show')
  }

  if (
    !(perms['customer'] & PERMISSION_FLAGS['read']) &&
    !(perms['admin'] & PERMISSION_FLAGS['orgRead']) &&
    !(perms['admin'] & PERMISSION_FLAGS['orgWrite'])
  ) {
    return t('api.community_management.permissions.hide')
  }

  return ''
}

export const getUsersForMultiselect = (state) => () => {
  const options = []
  for (const id in state.users) {
    options.push({ label: state.users[id].email, value: id })
  }
  return options
}

export const getCertifiedPartnerUsers = (state) =>
  Object.values(state.users).filter((user) => user.certifiedPartnerIdentification)

export const getCertifiedPartnerDataForUser = (state) => {
  return (userId) => {
    if (!state.users[userId] || !state.users[userId].certifiedPartnerIdentification) return {}

    return state.users[userId].certifiedPartnerIdentification
  }
}
